import { addToast } from '@heroui/react';
import { MessageComposer } from '@/features/MessageComposer';
import {
  Submission,
  TurnitinAntiplagiarism,
  TurnitinEulaModal,
  useTurnitinEulaCheck,
} from '@/features/AssignmentSubmission';
import {
  AssignmentSubmissionStatusEnum,
  IAssignmentContextAssignment,
  IAssignmentContextResponse,
  IAssignmentSubmissionDetails,
  IAssignmentSubmissionRequest,
  IAssignmentSubmissionStudentInfo,
  useSubmitSubmissionMutation,
  useUpdateSubmissionMutation,
} from '@/entities/Assignment';
import { getCourseDebtError } from '@/shared/lib/utils/getCourseDebtError';
import { getErrorMessage } from '@/shared/lib/utils/getErrorMessage';
import type { RejectApiResponse } from '@/shared/types/common';
import { LessonTypeEnum } from '@/shared/types/lesson';
import { SubmissionComments } from '../SubmissionComments/SubmissionComments';

/**
 * После `.unwrap()` сюда прилетает нормализованный `RejectApiResponse`, а не тело ответа,
 * поэтому сообщение читается из `message`.
 */
const notifySubmissionError = (error: unknown, fallback: string) => {
  const debt = getCourseDebtError(error);

  if (debt) {
    addToast({
      title: debt.message,
      description: 'Open your profile to see the outstanding balance and payment details.',
      color: 'danger',
    });
    return;
  }

  const message = getErrorMessage(error as RejectApiResponse | undefined);

  if (message && (message.includes('EULA') || message.includes('Turnitin'))) {
    addToast({
      title: 'Turnitin EULA verification failed. Please try again.',
      color: 'danger',
    });
    return;
  }

  addToast({
    title: message || fallback,
    color: 'danger',
  });
};

/** Контекст с гарантированным assignment (родитель рендерит только при assignmentContext.assignment). */
type AssignmentContextWithAssignment = Omit<IAssignmentContextResponse, 'assignment'> & {
  assignment: IAssignmentContextAssignment;
};

interface IAssignmentSubmissionProps {
  student: IAssignmentSubmissionStudentInfo | null;
  submission: IAssignmentSubmissionDetails | null;
  assignmentContext: AssignmentContextWithAssignment;
}

export const AssignmentSubmission = ({
  student,
  submission,
  assignmentContext,
}: IAssignmentSubmissionProps) => {
  const isSummative = assignmentContext.lesson.type !== LessonTypeEnum.Regular;
  const [submitAssignment, { isLoading: isSubmitLoading }] = useSubmitSubmissionMutation();
  const [updateSubmission, { isLoading: isUpdateLoading }] = useUpdateSubmissionMutation();
  const {
    isEulaModalOpen,
    isAccepting,
    isChecking,
    eulaHtmlContent,
    checkBeforeSubmit,
    closeEulaModal,
    handleEulaLoaded,
    handleEulaAccept,
  } = useTurnitinEulaCheck();

  const handleSubmitSubmission = async (data: IAssignmentSubmissionRequest) => {
    const needsPlagiarismCheck = Boolean(isSummative && data.files && data.files.length > 0);
    if (needsPlagiarismCheck) {
      const canProceed = await checkBeforeSubmit(data, async (submissionData) => {
        await performSubmit(submissionData);
      });
      if (!canProceed) {
        // Модалка показана, отправка произойдет после принятия EULA
        return;
      }
    }

    // Если не нужна проверка или EULA уже принят - отправить сразу
    await performSubmit(data);
  };

  // Фактическая отправка submission
  const performSubmit = async (data: IAssignmentSubmissionRequest) => {
    try {
      const submissionData: IAssignmentSubmissionRequest = {
        ...data,
      };

      if (assignmentContext.lesson.groupExamResubmissionId) {
        submissionData.groupExamResubmissionId = assignmentContext.lesson.groupExamResubmissionId;
      } else if (assignmentContext.lesson.groupLessonId) {
        submissionData.groupLessonId = assignmentContext.lesson.groupLessonId;
      }

      await submitAssignment({
        assignmentId: assignmentContext.assignment.id,
        data: submissionData,
      }).unwrap();

      addToast({
        title: 'Assignment submitted successfully',
        color: 'success',
      });
    } catch (error) {
      notifySubmissionError(error, 'Failed to submit assignment');
    }
  };

  const handleUpdateSubmission = async (data: IAssignmentSubmissionRequest) => {
    const needsPlagiarismCheck = Boolean(isSummative && data.files && data.files.length > 0);
    if (needsPlagiarismCheck) {
      const canProceed = await checkBeforeSubmit(data, async (submissionData) => {
        await performUpdate(submissionData);
      });

      if (!canProceed) {
        return;
      }
    }
    await performUpdate(data);
  };

  // Фактическое обновление submission
  const performUpdate = async (data: IAssignmentSubmissionRequest) => {
    try {
      await updateSubmission({
        assignmentId: assignmentContext.assignment.id,
        data,
      }).unwrap();

      addToast({
        title: 'Assignment updated successfully',
        color: 'success',
      });
    } catch (error) {
      notifySubmissionError(error, 'Failed to update assignment');
    }
  };

  return (
    <>
      {submission && student ? (
        <>
          <Submission
            student={student}
            submission={submission}
            userRole="student"
            onUpdate={handleUpdateSubmission}
            isUpdating={isChecking || isUpdateLoading}
            isSummative={isSummative}
          />
          {submission.turnitin && (
            <TurnitinAntiplagiarism data={submission.turnitin} submissionId={submission.id} />
          )}
          <SubmissionComments
            comments={submission?.submissionComments ?? []}
            submissionId={submission.id}
            canAddComment={submission.assignmentStatus !== AssignmentSubmissionStatusEnum.Completed}
          />
        </>
      ) : (
        <MessageComposer
          withLayout
          draftKey={`assignment:submission:${assignmentContext.assignment.id}`}
          onSubmit={handleSubmitSubmission}
          isSubmitting={isChecking || isSubmitLoading}
          maxAttachments={isSummative ? 1 : 3}
        />
      )}

      {isSummative && isEulaModalOpen && eulaHtmlContent && (
        <TurnitinEulaModal
          isOpen={isEulaModalOpen}
          isLoading={isAccepting}
          htmlContent={eulaHtmlContent}
          onClose={closeEulaModal}
          onLoaded={handleEulaLoaded}
          onAccept={handleEulaAccept}
        />
      )}
    </>
  );
};
